import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';

const getFilesFromEvent = (dataTransfer?: DataTransfer): File[] => {
  const files: File[] = [];

  if (dataTransfer && dataTransfer.items) {
    // Use DataTransferItemList interface to access the file(s)
    for (let i = 0; i < dataTransfer.items.length; i++) {
      // If dropped items aren't files, reject them
      if (dataTransfer.items[i].kind === 'file') {
        const file = dataTransfer.items[i].getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }
  } else if (dataTransfer && dataTransfer.files) {
    // Use DataTransfer interface to access the file(s)
    for (let i = 0; i < dataTransfer.files.length || 0; i++) {
      const file = dataTransfer.files[i];
      if (file) {
        files.push(file);
      }
    }
  }

  return files;
};

export const FileInput = ({
  className,
  onChange,
  invalid,
  multiple = false
}: {
  className?: string;
  onChange: (files: File[]) => void;
  invalid?: boolean;
  multiple?: boolean;
}) => {
  const [dragging, setDragging] = React.useState(false);
  const [attachments, setAttachments] = React.useState<File[]>([]);
  const ref = React.useRef<HTMLInputElement>(null);

  const addFiles = (files: File[]) =>
    setAttachments((prev) => {
      const newAttachments = multiple ? [...prev, ...files] : [...files];
      onChange(newAttachments);
      return newAttachments;
    });

  const removeFile = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.stopPropagation();
    setAttachments((prev) => {
      const newAttachments = prev.filter((_, i) => i !== index);
      onChange(newAttachments);
      return newAttachments;
    });
  };

  const onDragEnter = (e: { dataTransfer?: DataTransfer; preventDefault: () => void }) => {
    e.preventDefault();
    setDragging(true);
  };

  const onDragLeave = (e: { dataTransfer?: DataTransfer; preventDefault: () => void }) => {
    e.preventDefault();
    setDragging(false);
  };

  const onDragOver = (e: { dataTransfer?: DataTransfer; preventDefault: () => void }) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const onDrop = (e: { dataTransfer?: DataTransfer; preventDefault: () => void }) => {
    e.preventDefault();
    setDragging(false);

    const files = getFilesFromEvent(e.dataTransfer);
    if (files.length > 0) {
      addFiles(files);
    }
  };

  return (
    <div data-slot="control">
      <Headless.Input
        ref={ref}
        type="file"
        onChange={(e) => addFiles(Array.from(e.currentTarget?.files || []))}
        multiple={multiple}
        className="hidden"
      />
      <div
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onClick={() => ref?.current?.click()}
        className={clsx([
          'ring-1 ring-inset rounded-lg p-4',
          'flex flex-col items-center justify-center cursor-pointer',
          'transition-all duration-100',
          invalid && 'ring-red-600',
          !invalid &&
            !dragging &&
            attachments.length === 0 &&
            'ring-neutral-950/10 hover:ring-neutral-950/20',
          (dragging || attachments.length > 0) && 'ring-2 ring-primary-500',
          className
        ])}
      >
        <div className="pointer-events-none flex flex-col items-center justify-center">
          <CloudArrowUpIcon className="size-8 text-neutral-500" />
          <p className="text-sm text-neutral-500">Drag and drop files here</p>
        </div>
        {attachments.length > 0 && (
          <div className="pt-2 flex flex-wrap flex-row gap-2 items-center justify-center">
            {attachments.map((attachment, i) => (
              <div
                key={attachment.name}
                className="rounded-full bg-neutral-100 px-3 py-1 cursor-default flex flex-row items-center justify-center gap-1"
              >
                <p className="text-sm text-neutral-500">{attachment.name}</p>
                <Headless.Button type="button" onClick={(e) => removeFile(e, i)}>
                  <XMarkIcon className="size-3" />
                </Headless.Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
